/**
 * STAR TOPOLOGY: Hub selection and tree assignment for group calls (Phase 1).
 *
 * Replaces full-mesh group call topology with a star where the hub peer
 * forwards media tracks between leaf peers. Each leaf maintains exactly
 * one connection to the hub, eliminating N×(N-1)/2 SDP renegotiation
 * storms that cause Firefox group call instability.
 *
 * Hub selection uses the resource tier system:
 *   - Tier 3 peers preferred (Chromium ≥8 GB, ≥4 cores)
 *   - Tier 2 acceptable (Chromium 4–8 GB)
 *   - Tier 1 peers are always leaves (Firefox, low-RAM)
 *   - Tiebreaker: lexicographically higher address wins
 *
 * Phase 2 will add relay promotion for groups exceeding hub capacity.
 */

import type { TreeRole, TreeAssignment } from './av-stream-types';
import { getTransferResourceProfile, type TransferResourceProfile } from './resource-tier';

// ─── Feature Flag ─────────────────────────────────────────────────
// Gate star topology behind localStorage flag for rollback safety.
// Default ON — set to 'false' to revert to legacy full mesh.

const STAR_TOPOLOGY_FLAG = 'moby_pref_starTopology';

export function isStarTopologyEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const val = localStorage.getItem(STAR_TOPOLOGY_FLAG);
  // Default ON (null = not set = enabled). Only 'false' disables.
  return val !== 'false';
}

export function setStarTopologyEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STAR_TOPOLOGY_FLAG, String(enabled));
}

// ─── Hub Capacity Limits ──────────────────────────────────────────
// Maximum children a hub can serve before relay promotion is needed.

const HUB_CAPACITY: Record<1 | 2 | 3, number> = {
  1: 2,   // Gracefully degraded — avoid if possible
  2: 4,   // Comfortable for audio, tight for video
  3: 8,   // Full group call support
};

export function getHubCapacity(tier: 1 | 2 | 3): number {
  return HUB_CAPACITY[tier];
}

// ─── Peer Capability Info ─────────────────────────────────────────

export interface PeerCapability {
  address: string;
  tier: 1 | 2 | 3;
  isConnected: boolean;
}

// ─── Hub Selection ────────────────────────────────────────────────

/**
 * Select the best hub peer for a group call.
 *
 * Priority:
 *   1. Highest resource tier
 *   2. Lexicographically higher address (deterministic tiebreaker)
 *
 * The initiator is preferred if they are equal to or higher than the
 * best alternative — avoids unnecessary hub promotion handshake.
 *
 * @param initiatorAddress - The peer starting the group call
 * @param initiatorTier - Resource tier of the initiator
 * @param groupPeers - All other group members with their capabilities
 * @returns Address of the selected hub peer
 */
export function selectHub(
  initiatorAddress: string,
  initiatorTier: 1 | 2 | 3,
  groupPeers: PeerCapability[]
): string {
  let bestAddress = initiatorAddress;
  let bestTier = initiatorTier;

  for (const peer of groupPeers) {
    if (!peer.isConnected) continue;

    if (
      peer.tier > bestTier ||
      (peer.tier === bestTier && peer.address > bestAddress)
    ) {
      bestAddress = peer.address;
      bestTier = peer.tier;
    }
  }

  console.log('[TREE_TOPOLOGY] Hub selected', {
    hub: bestAddress.slice(0, 8),
    hubTier: bestTier,
    initiator: initiatorAddress.slice(0, 8),
    initiatorTier,
    candidateCount: groupPeers.length,
    hubIsInitiator: bestAddress === initiatorAddress,
  });

  return bestAddress;
}

// ─── Tree Assignment ──────────────────────────────────────────────

/**
 * Build tree assignments for all peers in a group call.
 * Phase 1: simple star — one hub, everyone else is a leaf.
 *
 * @param hubAddress - Selected hub peer address
 * @param allPeerAddresses - All peer addresses in the group (including hub)
 * @returns Map of address -> TreeAssignment
 */
export function buildTreeAssignments(
  hubAddress: string,
  allPeerAddresses: string[]
): Map<string, TreeAssignment> {
  const assignments = new Map<string, TreeAssignment>();
  const children = allPeerAddresses.filter(a => a !== hubAddress);

  // Hub assignment
  assignments.set(hubAddress, {
    role: 'hub',
    hubAddress,
    parentAddress: hubAddress,       // Hub is its own parent (root)
    childAddresses: children,
  });

  // Leaf assignments
  for (const addr of children) {
    assignments.set(addr, {
      role: 'leaf',
      hubAddress,
      parentAddress: hubAddress,
      childAddresses: [],
    });
  }

  console.log('[TREE_TOPOLOGY] Tree assignments built', {
    hub: hubAddress.slice(0, 8),
    leafCount: children.length,
    leaves: children.map(a => a.slice(0, 8)),
  });

  return assignments;
}

// ─── Hub Re-election (Crash Recovery) ─────────────────────────────

/**
 * Deterministically elect a new hub from remaining peers.
 * Used when the current hub disconnects unexpectedly.
 *
 * All peers run the same algorithm independently and arrive at the
 * same result — no coordination message needed.
 *
 * @param remainingPeers - Peers still connected (excluding crashed hub)
 * @param localTier - Local peer's resource tier
 * @returns Address of the new hub, or null if no peers remain
 */
export function electNewHub(
  remainingPeers: PeerCapability[],
  localAddress: string,
  localTier: 1 | 2 | 3
): string | null {
  if (remainingPeers.length === 0) return null;

  // Include self in the candidate pool
  const allCandidates: PeerCapability[] = [
    { address: localAddress, tier: localTier, isConnected: true },
    ...remainingPeers,
  ];

  // Same selection as selectHub — highest tier, then lexicographic tiebreaker
  let bestAddress = allCandidates[0].address;
  let bestTier = allCandidates[0].tier;

  for (const peer of allCandidates) {
    if (
      peer.tier > bestTier ||
      (peer.tier === bestTier && peer.address > bestAddress)
    ) {
      bestAddress = peer.address;
      bestTier = peer.tier;
    }
  }

  console.log('[TREE_TOPOLOGY] New hub elected (crash recovery)', {
    newHub: bestAddress.slice(0, 8),
    newHubTier: bestTier,
    candidateCount: allCandidates.length,
    isLocal: bestAddress === localAddress,
  });

  return bestAddress;
}

// ─── Hub Capacity Check ───────────────────────────────────────────

/**
 * Check if the hub can accept another child peer.
 * Returns true if under capacity, false if relay promotion is needed.
 */
export function hubHasCapacity(
  hubTier: 1 | 2 | 3,
  currentChildCount: number
): boolean {
  return currentChildCount < HUB_CAPACITY[hubTier];
}

// ─── Diagnostic Utilities ─────────────────────────────────────────

export interface TreeDiagnostics {
  enabled: boolean;
  localRole: TreeRole | null;
  hubAddress: string | null;
  childCount: number;
  hubTier: 1 | 2 | 3 | null;
  hubCapacity: number | null;
  atCapacity: boolean;
}

/**
 * Get diagnostic info about current tree topology state.
 * Used for UI display and debugging.
 */
export function getTreeDiagnostics(
  session: { treeRole?: TreeRole; hubAddress?: string; forwardingTransceivers?: Map<string, any[]> } | null,
  hubTier?: 1 | 2 | 3
): TreeDiagnostics {
  if (!session || !session.treeRole) {
    return {
      enabled: isStarTopologyEnabled(),
      localRole: null,
      hubAddress: null,
      childCount: 0,
      hubTier: null,
      hubCapacity: null,
      atCapacity: false,
    };
  }

  const childCount = session.forwardingTransceivers?.size ?? 0;
  const tier = hubTier ?? null;
  const capacity = tier !== null ? HUB_CAPACITY[tier] : null;

  return {
    enabled: isStarTopologyEnabled(),
    localRole: session.treeRole,
    hubAddress: session.hubAddress ?? null,
    childCount,
    hubTier: tier,
    hubCapacity: capacity,
    atCapacity: capacity !== null ? childCount >= capacity : false,
  };
}
